import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Printer, Ruler, HeartPulse, Check, RotateCcw, Mail } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { WaveDivider } from "@/components/decor";
import { MEDIA } from "@/lib/media";

const PDF = "/downloads/Happy-Bulldog-Co-Free-Size-Guide-and-Care-Checklist.pdf";
const trackDownload = () => { api.post("/guide/download").catch(() => {}); };
const STORE_KEY = "hb_bulldog_guide";

const MEASURES = [
  { k: "neck", label: "Neck circumference", unit: "cm", hint: "Around the base of the neck where the collar naturally sits." },
  { k: "chest", label: "Chest circumference", unit: "cm", hint: "Around the widest part of the ribcage, just behind the front legs." },
  { k: "back", label: "Back length", unit: "cm", hint: "From the base of the neck to the base of the tail along the spine." },
  { k: "weight", label: "Current weight", unit: "kg", hint: "A recent weight as a reference — weight alone does not determine fit." },
];

const CHECKLIST = [
  { title: "Every day", colour: "#FFD93D", items: [
    "Refresh drinking water; provide a cool, well-ventilated resting spot.",
    "Offer measured meals suited to your dog's age and veterinary plan.",
    "Choose gentle activity in cool conditions; stop if breathing becomes laboured.",
    "Inspect facial folds and clean gently; dry thoroughly. Check the tail pocket if present and follow your vet's cleaning advice.",
    "Check eyes and skin for redness, discharge, soreness or unusual odour.",
    "Offer calm enrichment and brush teeth with dog-safe toothpaste.",
  ] },
  { title: "Each week", colour: "#4CC9F0", items: [
    "Brush the coat; check ears, paws and nails. Clean ears only as advised.",
    "Wash bedding and clean bowls, toys and walking gear.",
    "Check body condition and record weight regularly with your vet's guidance.",
  ] },
  { title: "Keep on the calendar", colour: "#FF6B9D", items: [
    "Book health checks, vaccinations and parasite prevention on your vet's schedule.",
    "Ask about noisy breathing, exercise intolerance, skin problems or eye changes.",
  ] },
];

const load = () => { try { return JSON.parse(localStorage.getItem(STORE_KEY) || "{}"); } catch { return {}; } };

function EmailGuideForm() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(() => localStorage.getItem("hb_guide_emailed") === "1");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/guide/request", { email: email.trim(), first_name: name.trim(), consent: true });
      localStorage.setItem("hb_guide_emailed", "1");
      setDone(true);
      toast.success("Your guide is on its way — check your inbox 🐾");
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || "That email doesn't look right.");
    } finally { setBusy(false); }
  };
  return (
    <div className="rounded-3xl bg-[#19324A] text-white p-6 sm:p-8 relative overflow-hidden print:hidden" data-testid="guide-email-card">
      <span className="absolute -right-6 -top-6 text-8xl opacity-10 select-none" aria-hidden="true">🐾</span>
      <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#FFD93D]">Send it to my inbox</p>
      <h3 className="font-display font-extrabold text-2xl mt-1" style={{ color: "#fff" }}>Email me the PDF</h3>
      <p className="text-sm text-white/75 mt-2 max-w-lg">Get the printable guide in your inbox, plus the occasional new Chewie & Solo adventure. No spam — Bulldogs don't like it either.</p>
      {done ? (
        <p className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#7ED957] text-[#19324A] font-display font-bold text-sm px-5 py-2.5" data-testid="guide-email-done"><Check className="w-4 h-4" /> Sent! Check your inbox (and the promotions tab).</p>
      ) : (
        <form onSubmit={submit} className="mt-5 flex flex-col sm:flex-row gap-2" data-testid="guide-email-form">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="First name (optional)" data-testid="guide-email-name"
            className="rounded-full bg-white text-[#19324A] text-sm px-4 py-3 outline-none sm:w-44" />
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" data-testid="guide-email-input"
            className="flex-1 rounded-full bg-white text-[#19324A] text-sm px-4 py-3 outline-none" />
          <button type="submit" disabled={busy} data-testid="guide-email-submit"
            className="rounded-full bg-[#FF6B9D] text-white font-display font-bold text-sm px-5 py-3 inline-flex items-center justify-center gap-2 hover:scale-105 transition-transform disabled:opacity-60">
            <Mail className="w-4 h-4" /> {busy ? "Sending…" : "Send me the guide"}
          </button>
        </form>
      )}
      <p className="text-[11px] text-white/50 mt-3">By requesting the guide you agree to receive occasional emails from The Happy Bulldog Co. Unsubscribe any time.</p>
    </div>
  );
}

export default function BulldogGuide() {
  useSeo("Free Bulldog Size Guide & Care Checklist", "Free printable from The Happy Bulldog Co.: measure your Bulldog for clothing, collars and harnesses, plus a daily care checklist.");
  const [state, setState] = useState(load);
  useEffect(() => { localStorage.setItem(STORE_KEY, JSON.stringify(state)); }, [state]);
  const set = (k) => (e) => setState((s) => ({ ...s, [k]: e.target.value }));
  const ticks = state.ticks || {};
  const toggle = (id) => setState((s) => ({ ...s, ticks: { ...(s.ticks || {}), [id]: !(s.ticks || {})[id] } }));
  const total = CHECKLIST.reduce((n, g) => n + g.items.length, 0);
  const done = Object.values(ticks).filter(Boolean).length;
  const Field = ({ k, label, cls = "" }) => (
    <label className={`block ${cls}`}><span className="text-xs font-bold text-[#19324A]/60 uppercase tracking-wide">{label}</span>
      <input value={state[k] || ""} onChange={set(k)} data-testid={`guide-${k}`} className="mt-1 w-full rounded-xl border-2 border-[#19324A]/10 bg-white px-3 py-2 text-sm text-[#19324A] outline-none focus:border-[#4CC9F0]" /></label>
  );

  return (
    <div data-testid="bulldog-guide-page" className="print:bg-white">
      <section className="relative bg-[#4CC9F0]/20 pt-14 pb-24 print:hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid lg:grid-cols-[1.2fr_1fr] gap-10 items-center">
          <div>
            <span className="inline-block bg-[#FF6B9D] text-white font-display font-bold text-xs tracking-[0.2em] uppercase px-4 py-1.5 rounded-full">Free printable</span>
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl text-[#19324A] mt-4 leading-[1.05]">Bulldog Size Guide<br />& Care Checklist</h1>
            <p className="text-[#19324A]/70 text-base md:text-lg mt-4 max-w-xl">Big chest. Short body. Perfectly bulldog. Measure your dog before choosing clothing, a collar or a harness — then keep them happy with a little daily care. Kindness looks good on you!</p>
            <div className="flex flex-wrap gap-3 mt-7">
              <a href={PDF} download onClick={trackDownload} className="btn-pink inline-flex items-center gap-2" data-testid="guide-download"><Download className="w-5 h-5" /> Download the PDF</a>
              <button type="button" onClick={() => window.print()} className="btn-outline inline-flex items-center gap-2" data-testid="guide-print"><Printer className="w-5 h-5" /> Print this page</button>
            </div>
            <p className="text-xs text-[#19324A]/50 mt-3">Two pages · A4 · fill it in on screen or on paper.</p>
          </div>
          <div className="relative hidden lg:block">
            <img src={MEDIA.chewie} alt="Chewie the British Bulldog" className="w-72 h-72 object-cover rounded-[40%] mx-auto soft-shadow border-8 border-white hb-float" />
            <span className="absolute -top-4 right-10 bg-[#FFD93D] text-[#19324A] font-display font-bold text-sm px-4 py-2 rounded-full rotate-6 soft-shadow">Measure twice 📏</span>
          </div>
        </div>
        <WaveDivider color="#FFF9EE" className="absolute -bottom-px left-0 right-0" />
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 print:py-4 space-y-10">
        {/* ---- Size guide ---- */}
        <article className="bg-white rounded-3xl soft-shadow p-6 sm:p-8 print:shadow-none print:border print:break-after-page" data-testid="guide-size-section">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-11 h-11 rounded-2xl bg-[#4CC9F0]/20 flex items-center justify-center"><Ruler className="w-5 h-5 text-[#19324A]" /></span>
            <div><p className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#FF6B9D]">Page 1 of 2</p><h2 className="font-display font-extrabold text-2xl text-[#19324A]">Bulldog Size Guide</h2></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            <Field k="dog" label="Dog's name" /><Field k="date" label="Date" />
          </div>

          <h3 className="font-display font-bold text-lg text-[#19324A] flex items-center gap-2"><span className="text-[#4CC9F0]">01</span> How to measure</h3>
          <p className="text-sm text-[#19324A]/75 mt-2">Stand your dog comfortably on all four paws. Use a soft tape and keep it snug without pulling tight. Measure twice and record in centimetres.</p>
          <ul className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-[#19324A]/80">
            {MEASURES.map((m) => <li key={m.k}><strong className="text-[#19324A]">{m.label.split(" ")[0]}:</strong> {m.hint}</li>)}
          </ul>

          <h3 className="font-display font-bold text-lg text-[#19324A] flex items-center gap-2 mt-8"><span className="text-[#4CC9F0]">02</span> My bulldog's measurements</h3>
          <div className="mt-3 rounded-2xl overflow-hidden border border-[#19324A]/10" data-testid="guide-measure-table">
            <table className="w-full text-sm">
              <thead className="bg-[#19324A] text-white"><tr><th className="text-left px-4 py-2.5 font-display text-xs uppercase tracking-wider">Measurement</th><th className="text-left px-4 py-2.5 font-display text-xs uppercase tracking-wider">Input</th><th className="text-left px-4 py-2.5 font-display text-xs uppercase tracking-wider w-16">Unit</th></tr></thead>
              <tbody>{MEASURES.map((m, i) => (
                <tr key={m.k} className={i % 2 ? "bg-[#4CC9F0]/10" : ""}>
                  <td className="px-4 py-2 font-semibold text-[#19324A]">{m.label}</td>
                  <td className="px-4 py-2"><input inputMode="decimal" value={state[m.k] || ""} onChange={set(m.k)} data-testid={`guide-${m.k}`} className="w-full max-w-[160px] rounded-lg border-2 border-[#19324A]/10 bg-white px-3 py-1.5 outline-none focus:border-[#4CC9F0] print:border-b print:border-x-0 print:border-t-0 print:rounded-none" /></td>
                  <td className="px-4 py-2 text-[#19324A]/60">{m.unit}</td>
                </tr>))}</tbody>
            </table>
          </div>

          <h3 className="font-display font-bold text-lg text-[#19324A] flex items-center gap-2 mt-8"><span className="text-[#4CC9F0]">03</span> Choose the right fit</h3>
          <div className="mt-2 space-y-3 text-sm text-[#19324A]/75">
            <p>Use the specific product's size chart: sizes vary between brands and styles. There is no universal bulldog S/M/L size. For clothing, check the chest first, then neck and back length. If between sizes, follow that seller's guidance.</p>
            <p>For collars and harnesses, follow the maker's measuring points and adjustment instructions. Check that your dog can move freely, that straps do not rub behind the legs, and that the fit prevents slipping out. Recheck growing puppies often.</p>
          </div>
          <p className="mt-6 text-xs text-[#19324A]/50 print:hidden">Shopping for a bandana or bow tie? See our <Link to="/size-guide?tab=dogs" className="text-[#4CC9F0] font-semibold">dog size chart</Link>.</p>
        </article>

        {/* ---- Care checklist ---- */}
        <article className="bg-white rounded-3xl soft-shadow p-6 sm:p-8 print:shadow-none print:border" data-testid="guide-care-section">
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <span className="w-11 h-11 rounded-2xl bg-[#FF6B9D]/15 flex items-center justify-center"><HeartPulse className="w-5 h-5 text-[#FF6B9D]" /></span>
            <div className="flex-1"><p className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#FF6B9D]">Page 2 of 2</p><h2 className="font-display font-extrabold text-2xl text-[#19324A]">Bulldog Care Checklist</h2></div>
            <div className="flex items-center gap-2 print:hidden">
              <span className="text-xs font-bold text-[#19324A]/60 bg-[#FFF9EE] rounded-full px-3 py-1.5" data-testid="guide-progress">{done}/{total} ticked</span>
              {done > 0 && <button type="button" onClick={() => setState((s) => ({ ...s, ticks: {} }))} className="text-xs font-bold text-[#4CC9F0] inline-flex items-center gap-1" data-testid="guide-reset"><RotateCcw className="w-3.5 h-3.5" /> Fresh copy</button>}
            </div>
          </div>
          <p className="text-sm text-[#19324A]/75 mb-6">A little daily care for a happy bulldog. Tick as you go, then start a fresh copy whenever you need one.</p>
          <div className="grid md:grid-cols-2 gap-6">
            {CHECKLIST.map((g, gi) => (
              <div key={g.title} className={gi === 0 ? "md:row-span-2" : ""}>
                <h3 className="font-display font-bold text-lg text-[#19324A] inline-block px-3 py-1 rounded-full" style={{ background: `${g.colour}40` }}>{g.title}</h3>
                <ul className="mt-3 space-y-2">
                  {g.items.map((txt, i) => { const id = `${gi}-${i}`; const on = !!ticks[id]; return (
                    <li key={id}>
                      <button type="button" onClick={() => toggle(id)} aria-pressed={on} data-testid={`guide-tick-${id}`}
                        className="w-full text-left flex items-start gap-3 rounded-2xl px-3 py-2.5 hover:bg-[#FFF9EE] transition-colors">
                        <span className={`mt-0.5 w-5 h-5 shrink-0 rounded-md border-2 flex items-center justify-center transition-colors ${on ? "bg-[#7ED957] border-[#7ED957]" : "border-[#19324A]/30 bg-white"}`}>{on && <Check className="w-3.5 h-3.5 text-white" />}</span>
                        <span className={`text-sm ${on ? "text-[#19324A]/45 line-through" : "text-[#19324A]/85"}`}>{txt}</span>
                      </button>
                    </li>); })}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-8 rounded-3xl bg-[#FF6B9D]/10 border-2 border-[#FF6B9D]/30 p-5" data-testid="guide-emergency">
            <h3 className="font-display font-bold text-lg text-[#19324A]">Know when to act</h3>
            <p className="text-sm text-[#19324A]/80 mt-1">Breathing distress, collapse or suspected heatstroke need urgent veterinary care. Move to a cool place, start cooling with cool water and airflow, and contact a vet immediately. <strong>Never leave your dog in a parked car.</strong></p>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <Field k="vet" label="Vet" /><Field k="vet_phone" label="Phone" /><Field k="evet" label="Emergency vet" /><Field k="evet_phone" label="Phone" />
            </div>
          </div>
          <p className="mt-6 text-[11px] text-[#19324A]/50">General care guidance; your vet can tailor it to your bulldog. Sources: PDSA: Bulldog; RSPCA: preventing heatstroke. Reviewed October 2026.</p>
        </article>

        <EmailGuideForm />

        <div className="flex flex-wrap items-center justify-between gap-4 print:hidden">
          <p className="text-sm text-[#19324A]/60">The Happy Bulldog Co™ | A Chewie & Solo Adventure</p>
          <a href={PDF} download onClick={trackDownload} className="btn-outline inline-flex items-center gap-2 !py-2.5" data-testid="guide-download-bottom"><Download className="w-4 h-4" /> Download the printable PDF</a>
        </div>
      </section>
    </div>
  );
}
